import { faMapSigns } from '@fortawesome/free-solid-svg-icons';
import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import useAxiosHook from '../../../../hooks/useAxiosHook';
import { useEndpointGuard } from '../../../../hooks/useEndpointGuard';
import usePageTitle from '../../../../hooks/usePageTitle';
import useRouteDialog from '../../../../hooks/useRouteDialog';
import useTableColumns from '../../../../hooks/useTableColumns';
import useTableState from '../../../../hooks/useTableState';
import { WithPagination } from '../../../../types/api';
import { EntityIdRouteParams } from '../../../../types/routing';
import * as sortingMapsGuard from '../../../../utils/constants/auth/sortingMaps';
import {
  RoutePaths,
  constructIdRoute,
} from '../../../../utils/constants/routePaths';
import { sequential } from '../../../../utils/helpers/functions';
import { queryString } from '../../../../utils/helpers/http';
import { httpQueryObject } from '../../../../utils/helpers/misc';
import Table from '../../../DataTable/Table/Table';
import Flex from '../../../layout/flex/Flex';
import MainContent from '../../../layout/flex/MainContent';
import Sidebar from '../../../layout/flex/Sidebar';
import GoBackWithHistory from '../../../Misc/GoBack/GoBackWithHistory';
import Filters from '../../Components/Filters/Filters';
import HeaderPages from '../../Components/HeaderPages/HeaderPages';
import { SortingMachinesType } from '../SortingMachine.functions';
import CreateDialogSortingMap from './Dialogs/CreateDialogSortingMap/CreateDialogSortingMap';
import DeleteDialogMaps from './Dialogs/Delete/DeleteDialogMaps';
import ViewDialogMaps from './Dialogs/View/ViewDialogMaps';
import {
  SortingMapsType,
  additionalColumnProperties,
  getColumnHeadersMap,
  tableStorageKey,
} from './SortingMaps.functions';
import useTableFilters from './useTableFilters';

function SortingMaps(): JSX.Element {
  const { id } = useParams<EntityIdRouteParams>();
  const { t } = useTranslation();
  usePageTitle(t('Sorting Maps'));
  const [action, setAction] = useState<string>('');
  const [caller, setCaller] =
    useState<'group-actions' | 'context-menu'>('context-menu');
  const readGuard = useEndpointGuard(
    sortingMapsGuard.resourceSortingMapsRoutes
  );
  const createGuard = useEndpointGuard(
    sortingMapsGuard.createSortingMapsRoutes
  );
  const editGuard = useEndpointGuard(sortingMapsGuard.editSortingMapsRoutes);
  const deleteGuard = useEndpointGuard(
    sortingMapsGuard.deleteSortingMapsRoutes
  );

  const {
    tableRef,
    page,
    setPage,
    limit,
    setLimit,
    sortField,
    sortOrder,
    selection,
    selectionMultiple,
    setSortField,
    setSortOrder,
    setSelection,
    contextMenuSelection,
    setSelectionMultiple,
    setContextMenuSelection,
  } = useTableState<SortingMapsType>(tableStorageKey);

  const { filters, headerFiltersCount, resetAllFilters, httpFiltersObj } =
    useTableFilters(page, setPage!, limit);

  const columnHeadersMap = useMemo(() => getColumnHeadersMap(t), [t]);

  const {
    data,
    error,
    isLoading,
    reload: reloadCollection,
  } = useAxiosHook<WithPagination<SortingMapsType[]>>(
    `/orders/sorting/machine/${id}/map` +
      queryString(httpQueryObject(httpFiltersObj)),
    { skipWhen: !id }
  );

  const { data: sortingMachine } = useAxiosHook<SortingMachinesType>(
    `/orders/sorting/machine/${id}`,
    {
      skipWhen: !id,
    }
  );

  const {
    show: showViewDialog,
    hide: hideViewDialog,
    isVisible: isViewDialogVisible,
  } = useRouteDialog(
    constructIdRoute(RoutePaths.SortingMapsPage, id),
    constructIdRoute(RoutePaths.ViewSortingMapsPage, id),
    readGuard
  );

  const {
    show: showCreateDialog,
    hide: hideCreateDialog,
    isVisible: isCreateDialogVisible,
  } = useRouteDialog(
    constructIdRoute(RoutePaths.SortingMapsPage, id),
    constructIdRoute(RoutePaths.CreateSortingMapsPage, id),
    createGuard
  );

  const {
    show: showEditDialog,
    hide: hideEditDialog,
    isVisible: isEditDialogVisible,
  } = useRouteDialog(
    constructIdRoute(RoutePaths.SortingMapsPage, id),
    constructIdRoute(RoutePaths.EditSortingMapsPage, id),
    editGuard
  );

  const {
    show: showDeleteDialog,
    hide: hideDeleteDialog,
    isVisible: isDeleteDialogVisible,
  } = useRouteDialog(
    constructIdRoute(RoutePaths.SortingMapsPage, id),
    constructIdRoute(RoutePaths.DeleteSortingMapsPage, id),
    deleteGuard
  );

  const { selectedColumns, setSelectedColumns, columnOptions, columns } =
    useTableColumns(
      page,
      limit,
      'clients',
      columnHeadersMap,
      columnHeadersMap,
      (c) =>
        additionalColumnProperties(
          t,
          c as keyof typeof columnHeadersMap,
          setContextMenuSelection,
          setAction,
          setCaller,
          readGuard,
          editGuard,
          deleteGuard
        )
    );

  const finalColumns = useMemo<JSX.Element[]>(
    () => [
      ...columns,
      <Column
        key="action-column"
        header={t('Actions')}
        field="actions"
        frozen
        alignFrozen="right"
        {...additionalColumnProperties(
          t,
          'actions',
          setContextMenuSelection,
          setAction,
          setCaller,
          readGuard,
          editGuard,
          deleteGuard
        )}
      />,
    ],
    [
      columns,
      t,
      setContextMenuSelection,
      setAction,
      setCaller,
      readGuard,
      editGuard,
      deleteGuard,
    ]
  );

  useEffect(() => {
    if (action && contextMenuSelection) {
      setCaller('context-menu');

      if (action === 'view-details') {
        showViewDialog();
      }
      if (action === 'edit') {
        showEditDialog();
      }
      if (action === 'delete') {
        showDeleteDialog();
      }

      setAction('');
    }
  }, [
    action,
    contextMenuSelection,
    showViewDialog,
    showEditDialog,
    showDeleteDialog,
    setAction,
  ]);

  function handleCreateLogisticsRoute() {
    setContextMenuSelection(undefined);
    showCreateDialog();
  }

  function handleEditButton(data: SortingMapsType) {
    setContextMenuSelection(data);
    showEditDialog();
  }

  function handleViewHideButton() {
    setContextMenuSelection(undefined);
    hideViewDialog();
  }

  function handleCreateEditHideButton() {
    setContextMenuSelection(undefined);
    sequential(hideEditDialog, hideCreateDialog);
  }

  return (
    <div className="page sorting-maps">
      <GoBackWithHistory to="/sorting-machines" page={t('Sorting Machines')} />
      <HeaderPages
        title={t('Sorting Maps')}
        subtitle={t('Sorting maps management')}
        icon={faMapSigns}
      >
        {createGuard && (
          <div>
            <Button
              label={t('Create sorting map')}
              icon="fas fa-plus"
              className="main-btn"
              onClick={handleCreateLogisticsRoute}
              data-cy="create-btn"
            />
          </div>
        )}
      </HeaderPages>

      <ViewDialogMaps
        isShown={isViewDialogVisible}
        onHide={handleViewHideButton}
        data={contextMenuSelection}
        isEditBtnShown
        setIsEditDialog={handleEditButton}
      />

      <CreateDialogSortingMap
        isEditDialog={isEditDialogVisible}
        isShown={isCreateDialogVisible || isEditDialogVisible}
        onHide={handleCreateEditHideButton}
        data={contextMenuSelection}
        sortingMachine={sortingMachine}
        reloadCollection={reloadCollection}
      />

      <DeleteDialogMaps
        visible={isDeleteDialogVisible}
        data={
          caller === 'context-menu'
            ? contextMenuSelection
              ? [contextMenuSelection]
              : []
            : selectionMultiple
        }
        onHide={() => hideDeleteDialog()}
        reloadSortingMachine={reloadCollection}
        setSelectionMultiple={setSelectionMultiple}
        setContextMenuSelection={setContextMenuSelection}
      />

      <Flex direction="column">
        <Sidebar>
          <Filters
            filters={filters}
            resetAllFilters={resetAllFilters}
            filterHeight={180}
          />
        </Sidebar>
        <MainContent>
          <Table
            tableRef={tableRef}
            columns={finalColumns}
            data={data}
            isLoading={isLoading}
            hasError={!!error}
            reload={reloadCollection}
            headerTitle=""
            filterHeight={180}
            headerFiltersCount={headerFiltersCount}
            onHeaderFiltersResetAllBtnClick={resetAllFilters}
            setPage={setPage}
            setLimit={setLimit}
            sortField={sortField}
            rows={limit}
            setSortField={setSortField}
            setSortOrder={setSortOrder}
            setSelection={setSelection}
            sortOrder={sortOrder}
            selection={selection}
            storageString={tableStorageKey}
            selectedColumns={selectedColumns}
            setSelectedColumns={setSelectedColumns}
            columnOptions={columnOptions}
            onRowDoubleClick={showViewDialog}
            contextMenuSelection={contextMenuSelection}
            setContextMenuSelection={setContextMenuSelection}
          />
        </MainContent>
      </Flex>
    </div>
  );
}

export default SortingMaps;
